import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { gymInfo } from '../mock';
import './ContactPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Prepare contact data
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        subject: formData.subject || '',
        message: formData.message
      };

      // Make API call to backend
      const response = await axios.post(`${API}/contact`, contactData);

      if (response.data) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    }
  };

  return (
    <div className="contact-page">
      <Navbar />
      
      <div className="page-hero">
        <h1>CONTACT US</h1>
        <p>Get in touch with our team. We're here to help you start your boxing journey.</p>
      </div>

      <section className="section">
        <div className="contact-container">
          <div className="contact-form-section">
            <h2>Send Us A Message</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-grid">
                <div className="form-group">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group full-width">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="form-textarea"
                    placeholder="Tell us how we can help you..."
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary full-width">
                Send Message
                <Send size={20} style={{ marginLeft: '8px' }} />
              </button>
            </form>
          </div>

          <div className="contact-info-section">
            <h2>Get In Touch</h2>
            
            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="info-icon">
                  <MapPin size={28} />
                </div>
                <div className="info-content">
                  <h4>Location</h4>
                  <p>{gymInfo.address}</p>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="info-icon">
                  <Phone size={28} />
                </div>
                <div className="info-content">
                  <h4>Phone</h4>
                  <p>{gymInfo.phone}</p>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="info-icon">
                  <Mail size={28} />
                </div>
                <div className="info-content">
                  <h4>Email</h4>
                  <p>{gymInfo.email}</p>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="info-icon">
                  <Clock size={28} />
                </div>
                <div className="info-content">
                  <h4>Hours</h4>
                  <p>
                    <strong>Mon-Fri:</strong> {gymInfo.hours.weekday}<br />
                    <strong>Sat:</strong> {gymInfo.hours.saturday}<br />
                    <strong>Sun:</strong> {gymInfo.hours.sunday}<br />
                    <strong>Public Holidays:</strong> {gymInfo.hours.publicHolidays}
                  </p>
                </div>
              </div>
            </div>

            <div className="map-section">
              <h3>Find Us</h3>
              <div className="map-container">
                <iframe
                  title="Gym Location"
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3459.6892765432!2d31.052420940164737!3d-29.774613901205846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjnCsDQ2JzI4LjYiUyAzMcKwMDMnMDguNyJF!5e0!3m2!1sen!2sza!4v1640000000000!5m2!1sen!2sza"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ContactPage;